import { Form } from "@/pages/configuration/pages/forms/models/Form";
import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { getForms } from "../repositories/FormRepository";

type Props = {
	label?: string;
	value: number | null;
	onChange?: (id: number | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
};

const useForms = () => {
	return useQuery<Form[], Error>({
		queryKey: ["formsByUser"],
		queryFn: ({ signal }) => getForms(signal),
	});
};

export const FormSelector = ({
	label = "Form",
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: forms, isLoading, isError, error: e } = useForms();

	const selectedForm = forms?.find((type) => type.FormId === value) || null;

	useEffect(() => {
		if (isError) {
			setError(e.message);
		}
	}, [isError, e]);

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={forms || []}
				getOptionDisabled={(option) => option.IsSystemForm}
				getOptionLabel={(option) => option.FormName}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>{params.InputProps.endAdornment}</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedForm}
				onChange={(event, newValue) => {
					onChange(newValue?.FormId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading forms"
			/>
		</>
	);
};
